/**
 * ClassesEmptyState - Empty state component for classes page
 */

"use client";

import { Flex } from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Heading, Text, Card } from "@/components/ui";
import { useTenantData } from "@/hooks";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { ContentSettingsText } from "@/lib/utils/content-settings/ContentSettingsText";
import { getTenantDisplayName } from "@/lib/utils/tenant";

interface ClassesEmptyStateProps {
  variant: "no-seasons" | "no-classes" | "no-results";
  suggestions?: string[];
}

export const ClassesEmptyState = ({ variant, suggestions }: ClassesEmptyStateProps) => {
  const theme = useThemeTokens();
  const { tenantId, tenantData } = useTenantData();
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: !!tenantId },
  );

  const studioName = getTenantDisplayName(tenantData);
  const tenantEmail = tenantData?.email ?? null;

  if (variant === "no-seasons") {
    return (
      <Flex
        px="10px"
        minH="50vh"
        align="center"
        justify="center"
        textAlign="center"
        direction="column"
      >
        <Heading size="xl" mb={2}>
          {contentSettings.classesNoSeasonsTitle}
        </Heading>
        <ContentSettingsText
          variant="secondary"
          fontSize="md"
          maxW="600px"
          studioName={studioName}
          tenantEmail={tenantEmail}
        >
          {contentSettings.classesNoSeasonsDescription}
        </ContentSettingsText>
      </Flex>
    );
  }

  if (variant === "no-classes") {
    return (
      <Flex
        px="10px"
        minH="50vh"
        align="center"
        justify="center"
        textAlign="center"
        direction="column"
      >
        <Heading size="xl" mb={2}>
          {contentSettings.classesNoClassesTitle}
        </Heading>
        <ContentSettingsText
          variant="secondary"
          fontSize="md"
          maxW="600px"
          studioName={studioName}
          tenantEmail={tenantEmail}
        >
          {contentSettings.classesNoClassesDescription}
        </ContentSettingsText>
      </Flex>
    );
  }

  const defaultSuggestions = contentSettings.classesNoResultsSuggestions
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const displaySuggestions = suggestions || defaultSuggestions;

  return (
    <Card
      mt="20px"
      p={12}
      textAlign="center"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor={theme.borderGlass.default}
      minH="300px"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Flex direction="column" gap={4} align="center">
        <Text fontSize="lg" fontWeight="600" color={theme.text.primary}>
          {contentSettings.classesNoResultsTitle}
        </Text>
        {displaySuggestions.length > 0 && (
          <Flex direction="column" gap={2}>
            <Flex direction="column" gap={1.5} textAlign="left">
              {displaySuggestions.map((suggestion, index) => (
                <Text
                  key={index}
                  fontSize="sm"
                  color={theme.text.tertiary}
                  _before={{
                    content: '"• "',
                    color: theme.text.tertiary,
                  }}
                >
                  {suggestion}
                </Text>
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
